// Schema microdata utility for Pharmacy Wiki

// Static schema data that can be injected server-side
export const getStaticSchemaData = (type, data, baseUrl = '') => {
  return generateSchemaMicrodata(type, data, baseUrl);
};

export const generateSchemaMicrodata = (type, data, baseUrl = null) => {
  const currentBaseUrl = baseUrl || (typeof window !== 'undefined' ? window.location.origin : '');
  
  switch (type) {
    case 'website':
      return {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Pharmacy Wiki",
        "description": "Comprehensive pharmaceutical information platform providing detailed information about drugs, manufacturers, and ingredients",
        "url": currentBaseUrl,
        "potentialAction": {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": `${currentBaseUrl}/search?q={search_term_string}`
          },
          "query-input": "required name=search_term_string"
        },
        "publisher": {
          "@type": "Organization",
          "name": "Pharmacy Wiki",
          "url": currentBaseUrl
        }
      };

    case 'organization':
      return {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Pharmacy Wiki",
        "description": "Pharmaceutical information platform for healthcare professionals and patients",
        "url": currentBaseUrl,
        "logo": `${currentBaseUrl}/logo.svg`,
        "sameAs": [
          "https://pharamcypilot.info"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "email": "support@pharmacy.wiki"
        }
      };

    case 'ingredient':
      return {
        "@context": "https://schema.org",
        "@type": "Drug",
        "name": data.name,
        "description": `Pharmaceutical ingredient ${data.name} - Lists of pharmaceutical products containing this ingredient`,
        "url": `${currentBaseUrl}/ingredients/${data.uri}`,
        "category": "Active Ingredient",
        "drugClass": "Active Ingredient",
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "seller": {
            "@type": "Organization",
            "name": "Authorized Pharmacies",
            "description": "Available through authorized pharmacies and healthcare providers"
          },
          "description": "Available by prescription through authorized pharmacies and healthcare providers",
          "category": "Active Ingredient"
        },
        "hasPart": data.tradenames?.map(product => ({
          "@type": "Drug",
          "name": product.name,
          "url": `${currentBaseUrl}/tradenames/${product.name?.toLowerCase().replace(/\s+/g, '-')}`,
          "isPartOf": {
            "@type": "Drug",
            "name": data.name
          },
          "offers": {
            "@type": "Offer",
            "availability": "https://schema.org/InStock",
            "seller": {
              "@type": "Organization",
              "name": "Authorized Pharmacies",
              "description": "Available through authorized pharmacies and healthcare providers"
            },
            "description": "Available by prescription through authorized pharmacies and healthcare providers",
            "category": "Pharmaceutical Product"
          }
        })) || [],
        "numberOfItems": data.pagination?.totalItems || 0,
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `${currentBaseUrl}/ingredients/${data.uri}`
        }
      };

    case 'manufacturer':
      return {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": data.manufacturer.name,
        "description": `Pharmaceutical manufacturer ${data.manufacturer.name} - Pharmaceutical products and active ingredients`,
        "url": data.manufacturer.website || `${currentBaseUrl}/manufacturers/${data.manufacturer.uri}`,
        "logo": data.manufacturer.logo_url || `${currentBaseUrl}/logo.svg`,
        "knowsAbout": [
          "Pharmaceutical Manufacturing",
          "Drug Development",
          "Active Ingredients",
          "Pharmaceutical Products"
        ],
        "makesOffer": data.tradenames?.filter(product => product !== null && product !== undefined).map(product => ({
          "@type": "Offer",
          "itemOffered": {
            "@type": "Drug",
            "name": product.name,
            "url": `${currentBaseUrl}/tradenames/${product.uri}`,
            "manufacturer": {
              "@type": "Organization",
              "name": data.manufacturer.name
            },
          }
        })) || [],
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `${currentBaseUrl}/manufacturers/${data.uri}`
        }
      };

    case 'product':
      return {
        "@context": "https://schema.org",
        "@type": "Drug",
        "name": data.name,
        "description": data.description || `Pharmaceutical product ${data.name}`,
        "url": `${currentBaseUrl}/tradenames/${data.name?.toLowerCase().replace(/\s+/g, '-')}`,
        "image": `${currentBaseUrl}/logo.svg`,
        "category": "Pharmaceutical Product",
        "drugClass": "Prescription Drug",
        "activeIngredient": data.ingredients?.map(ing => ing.name).join(", ") || "",
        "dosageForm": data.forms === 'object' ? data.forms : data.forms?.length > 0 ? `${data.forms[0][0]}` : "Not specified",
        "manufacturer": data.manufacturers ? data.manufacturers.map(manufacturer => ({
          "@type": "Organization",
          "name": manufacturer.name,
          "url": `${currentBaseUrl}/manufacturers/${manufacturer.uri}`
        })) : [],
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "seller": {
            "@type": "Organization",
            "name": "Authorized Pharmacies",
            "description": "Available through authorized pharmacies and healthcare providers"
          },
          "description": "Available by prescription through authorized pharmacies and healthcare providers",
          "category": "Pharmaceutical Product"
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `${currentBaseUrl}/tradenames/${data.name?.toLowerCase().replace(/\s+/g, '-')}`
        }
      };

    case 'breadcrumb':
      return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": data.items?.map((item, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": item.name,
          "item": item.url
        })) || []
      };

    default:
      return null;
  }
};

// Helper function to inject schema microdata into the document head
export const injectSchemaMicrodata = (schemaData) => {
  // Only run in browser environment
  if (typeof window === 'undefined') return;
  
  // Remove existing schema script tags
  const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
  existingScripts.forEach(script => script.remove());

  // Create new script tag with schema data
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(schemaData);
  document.head.appendChild(script);
};

// Helper function to generate breadcrumb schema
export const generateBreadcrumbSchema = (items) => {
  return generateSchemaMicrodata('breadcrumb', { items });
};

// Helper function to generate FAQ schema
export const generateFAQSchema = (faqs) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

// Helper function to create schema script tag HTML
export const createSchemaScriptTag = (schemaData) => {
  if (!schemaData) return '';
  return `<script type="application/ld+json">${JSON.stringify(schemaData)}</script>`;
}; 